/**
 * Application entrypoint: loads runtime config, renders the appropriate app
 * state (configuration error / disabled / active referral form), and wires up
 * validation, submission, and TurboVets postMessage autofill.
 */
import "./styles.css";
import { ConfigError, loadConfig, type RuntimeConfig } from "./config";
import {
  createCorrelationId,
  createLeadSubmissionClient,
  friendlyReferralMessage,
  isRetryableKind,
  ReferralApiError,
  type ApiErrorKind,
  type LeadSubmissionClient,
} from "./api";
import { installAutofillListener } from "./autofill";
import {
  buildCompleteMessage,
  buildErrorMessage,
  buildPrefillAck,
  buildReadyMessage,
  createMessageId,
  notifyEmbedder,
} from "./messaging";
import type { LmdSubmissionResult } from "./models/lmd";
import { emitSubmitTelemetry, type TelemetrySink } from "./telemetry";
import { buildPayload } from "./form/payload-builder";
import { buildLmdUpsertRequest } from "./form/lmd-mapper";
import { readFormState, renderReferralForm } from "./form/referral-form";
import { validatePurchase, validateRefinance } from "./form/validation";
import { initTheme } from "./theme";
import { resolveEntryModeFromSearch, shouldRouteToWizard } from "./wizard/entry-routing";
import { mountJourneyExperience } from "./wizard/journey";
import type { LoanPurpose } from "./wizard/loan-purpose-selector";
import {
  readRefinancePrefillFromForm,
  translateLegacyMilitaryPrefill,
} from "./wizard/refinance-prefill";
import type { RefinanceSubmissionDeps } from "./wizard/refinance-submission";
import { renderRefinanceWizard } from "./wizard/refinance-wizard";

const BRAND_HEADER = `
  <header class="brand">
    <div class="brand-mark">loan<span>Depot</span></div>
    <div class="brand-sub">TurboVets Referral</div>
  </header>`;

export function renderConfigError(root: HTMLElement, message: string): void {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="state state-error" role="alert">
      <h1>Configuration error</h1>
      <p>The referral form could not start because its configuration is invalid.</p>
      <p class="detail">${escapeHtml(message)}</p>
    </main>`;
}

export function renderDisabled(root: HTMLElement): void {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="state state-disabled">
      <h1>Referral form unavailable</h1>
      <p>This referral form is temporarily unavailable. Please check back later.</p>
    </main>`;
}

export function renderAppShell(root: HTMLElement, config: RuntimeConfig): HTMLFormElement {
  root.innerHTML = `
    ${BRAND_HEADER}
    <main class="app">
      <p class="intro">Complete the referral below. Your details are reviewed before anything is sent to loanDepot.</p>
      <div id="prefill-notice" class="notice" hidden role="status"></div>
      <div id="form-mount"></div>
      <div id="validation-summary" class="validation-summary" hidden role="alert"></div>
      <div id="result" class="result" hidden aria-live="polite"></div>
      ${config.debug ? `<p class="env-tag">Environment: ${escapeHtml(config.environment)}</p>` : ""}
    </main>`;

  const mount = root.querySelector<HTMLElement>("#form-mount");
  if (!mount) throw new Error("Missing form mount point.");
  // Partner reference ID is a diagnostics-only field; it is rendered only when
  // the runtime debug flag is on and otherwise omitted entirely.
  return renderReferralForm(mount, { showPartnerReferenceId: config.debug });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function showValidationSummary(root: ParentNode, errors: string[]): void {
  const summary = root.querySelector<HTMLElement>("#validation-summary");
  if (!summary) return;
  if (errors.length === 0) {
    summary.hidden = true;
    summary.innerHTML = "";
    return;
  }
  summary.hidden = false;
  summary.innerHTML = `
    <h2>Please fix the following:</h2>
    <ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}</ul>`;
}

function showResult(root: ParentNode, html: string, kind: "success" | "error"): void {
  const result = root.querySelector<HTMLElement>("#result");
  if (!result) return;
  result.hidden = false;
  result.className = `result result-${kind}`;
  result.setAttribute("role", kind === "error" ? "alert" : "status");
  result.innerHTML = html;
}

/** Host label of the LeadIntake API base URL, never the key or full path. Debug-only. */
function apiHostLabel(config: RuntimeConfig): string {
  try {
    return new URL(config.leadIntakeApiBaseUrl).host;
  } catch {
    return config.leadIntakeApiBaseUrl;
  }
}

/**
 * Renders a non-secret diagnostics panel. Only shown when the runtime debug
 * flag is on; it never contains borrower PII, the API key, raw payloads, or
 * stack traces.
 */
function debugPanel(config: RuntimeConfig, rows: Array<[string, string | undefined]>): string {
  if (!config.debug) return "";
  const items = [
    ["Environment", config.environment],
    ["LeadIntake host", apiHostLabel(config)],
    ...rows,
  ]
    .filter((row): row is [string, string] => typeof row[1] === "string" && row[1] !== "")
    .map(([label, value]) => `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd>`)
    .join("");
  return `
    <details class="debug-panel" open>
      <summary>Diagnostics</summary>
      <dl>${items}</dl>
    </details>`;
}

function showPrefillNotice(root: ParentNode, count: number): void {
  const notice = root.querySelector<HTMLElement>("#prefill-notice");
  if (!notice) return;
  notice.hidden = false;
  notice.textContent = `We pre-filled ${count} field${count === 1 ? "" : "s"} from TurboVets. Please review everything before submitting.`;
}

export function wireSubmit(
  root: HTMLElement,
  form: HTMLFormElement,
  config: RuntimeConfig,
  fetchFn: typeof fetch = fetch,
  telemetrySink?: TelemetrySink,
): void {
  let activeCorrelationId = "";
  const client = createLeadSubmissionClient(config, {
    fetchFn,
    correlationIdFactory: () => activeCorrelationId,
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void handleSubmit(root, form, config, client, () => {
      activeCorrelationId = createCorrelationId();
      return activeCorrelationId;
    }, telemetrySink);
  });
}

async function handleSubmit(
  root: HTMLElement,
  form: HTMLFormElement,
  config: RuntimeConfig,
  client: LeadSubmissionClient,
  correlationIdFactory: () => string,
  telemetrySink?: TelemetrySink,
): Promise<void> {
  const resultEl = root.querySelector<HTMLElement>("#result");
  if (resultEl) {
    resultEl.hidden = true;
    resultEl.className = "result";
    resultEl.innerHTML = "";
  }

  const state = readFormState(form);
  const built = buildPayload(state);
  if (!built.ok) {
    showValidationSummary(root, [built.error]);
    return;
  }

  const errors =
    built.purpose === "purchase"
      ? validatePurchase(built.request)
      : validateRefinance(built.request);

  if (errors.length > 0) {
    showValidationSummary(root, errors);
    return;
  }
  showValidationSummary(root, []);

  const submitButton = form.querySelector<HTMLButtonElement>("#submit-referral");
  if (submitButton) submitButton.disabled = true;

  const correlationId = correlationIdFactory();
  const partnerReferenceId = built.request.partnerReferenceId;

  try {
    const response = await client.submitLead(buildLmdUpsertRequest(built));

    const messageId = createMessageId();
    renderCompletion(root, config, response, { correlationId, messageId, partnerReferenceId });

    emitSubmitTelemetry(
      { outcome: "success", correlationId },
      { enabled: config.telemetryEnabled, sink: telemetrySink },
    );

    notifyEmbedder(
      window,
      buildCompleteMessage({
        correlationId,
        response,
        environment: config.environment,
        debug: config.debug,
        partnerReferenceId,
        messageId,
      }),
      config.allowedPrefillOrigins,
    );
  } catch (err) {
    const kind: ApiErrorKind = err instanceof ReferralApiError ? err.kind : "unknown";
    const status = err instanceof ReferralApiError ? err.status : undefined;
    const retryable = isRetryableKind(kind);
    const message = friendlyReferralMessage(kind);
    const messageId = createMessageId();

    renderErrorState(root, config, {
      kind,
      status,
      retryable,
      message,
      correlationId,
      messageId,
      partnerReferenceId,
    });

    emitSubmitTelemetry(
      { outcome: kind, correlationId, status },
      { enabled: config.telemetryEnabled, sink: telemetrySink },
    );

    notifyEmbedder(
      window,
      buildErrorMessage({ correlationId, reason: kind }),
      config.allowedPrefillOrigins,
    );
  } finally {
    if (submitButton) submitButton.disabled = false;
  }
}

function renderCompletion(
  root: HTMLElement,
  config: RuntimeConfig,
  response: LmdSubmissionResult,
  ids: { correlationId: string; messageId: string; partnerReferenceId?: string },
): void {
  showResult(
    root,
    `
      <h2>Referral submitted</h2>
      <p>Thanks — your referral was submitted to loanDepot and accepted for review. No further action is needed right now.</p>
      <dl>
        ${ids.partnerReferenceId ? `<dt>Partner reference</dt><dd>${escapeHtml(ids.partnerReferenceId)}</dd>` : ""}
        <dt>Status</dt><dd>${escapeHtml(response.status)}</dd>
        <dt>Submitted at</dt><dd>${escapeHtml(response.submittedAt)}</dd>
      </dl>
      ${debugPanel(config, [
        ["Lead reference", response.leadGuid],
        ["Correlation ID", ids.correlationId],
        ["Message ID", ids.messageId],
      ])}`,
    "success",
  );
}

function renderErrorState(
  root: HTMLElement,
  config: RuntimeConfig,
  detail: {
    kind: ApiErrorKind;
    status?: number;
    retryable: boolean;
    message: string;
    correlationId: string;
    messageId: string;
    partnerReferenceId?: string;
  },
): void {
  const guidance = detail.retryable
    ? "You can try submitting again in a moment."
    : "Please contact loanDepot support for help with this referral.";

  showResult(
    root,
    `
      <h2>Referral not submitted</h2>
      <p>${escapeHtml(detail.message)}</p>
      <p class="retry-guidance">${escapeHtml(guidance)}</p>
      ${debugPanel(config, [
        ["Error kind", detail.kind],
        ["Status", detail.status !== undefined ? String(detail.status) : undefined],
        ["Retryable", String(detail.retryable)],
        ["Correlation ID", detail.correlationId],
        ["Message ID", detail.messageId],
      ])}`,
    "error",
  );
}

export interface BootstrapOptions {
  /** Optional telemetry sink; defaults to the module's sanitized console sink. */
  telemetrySink?: TelemetrySink;
}

export async function bootstrap(
  root: HTMLElement,
  fetchFn: typeof fetch = fetch,
  options: BootstrapOptions = {},
): Promise<void> {
  // Apply the visual theme first so every rendered state (config error,
  // disabled, active form) is styled consistently.
  if (typeof window !== "undefined") initTheme(window);

  let config: RuntimeConfig;
  try {
    config = await loadConfig(fetchFn);
  } catch (err) {
    const message = err instanceof ConfigError ? err.message : "Unexpected configuration failure.";
    renderConfigError(root, message);
    return;
  }

  if (!config.enabled) {
    renderDisabled(root);
    return;
  }

  // Entry routing (WI-TV-W65-001): both loan purposes are served by the shared
  // step journey. An explicit, normalized `?loanPurpose=purchase` or
  // `?loanPurpose=refinance` hint direct-routes to that journey's step 1; a
  // missing, blank, or unrecognized value shows the neutral two-option selector
  // with no purpose preselected. A prefill message whose applied `loanPurpose`
  // is refinance still routes into refinance from a mounted purchase journey,
  // and both journeys expose the shared `Change loan type` action.
  const search = typeof window !== "undefined" ? window.location.search : "";
  const mode = resolveEntryModeFromSearch(search);
  const entry: LoanPurpose | undefined =
    mode === "refinance-wizard" ? "refinance" : mode === "purchase-wizard" ? "purchase" : undefined;

  mountJourneyExperience({
    root,
    config,
    purpose: entry,
    search,
    fetchFn,
    ...(options.telemetrySink ? { telemetrySink: options.telemetrySink } : {}),
    ...(typeof window !== "undefined" ? { win: window } : {}),
  });
}

/**
 * Wiring the refinance wizard needs to perform its single deliberate LMD
 * submission: runtime config, the fetch implementation, the sanitized telemetry
 * sink, and the window whose embedder receives the sanitized outcome message.
 */
function refinanceSubmissionDeps(
  config: RuntimeConfig,
  fetchFn: typeof fetch,
  telemetrySink?: TelemetrySink,
): RefinanceSubmissionDeps {
  const deps: RefinanceSubmissionDeps = { config, fetchFn };
  if (telemetrySink) deps.telemetrySink = telemetrySink;
  if (typeof window !== "undefined") deps.win = window;
  return deps;
}

/**
 * Renders the legacy purchase referral form and wires the refinance entry
 * routing on top of it: a live loan-purpose selection of "Refinance" — or a
 * prefill that sets loan purpose to refinance — swaps the root over to the
 * refinance wizard.
 *
 * Wave 6 routes purchase into the purchase wizard, so this is no longer an entry
 * path. It is retained unchanged, with its submission, telemetry, embedder
 * messaging, and prefill behavior intact, until the purchase wizard's own
 * submission integration (WI-TV-W6-004) replaces it.
 */
export function mountPurchaseFormExperience(
  root: HTMLElement,
  config: RuntimeConfig,
  fetchFn: typeof fetch = fetch,
  telemetrySink?: TelemetrySink,
): void {
  const form = renderAppShell(root, config);
  wireSubmit(root, form, config, fetchFn, telemetrySink);

  let routed = false;
  // True only while a prefill payload is mid-application, so the loan-purpose
  // change it fires does not route into the wizard before the remaining
  // refinance values have been applied and could be carried over.
  let applyingPrefill = false;
  // Accepted prefill values that have no legacy form control to be read back
  // from (the application-level military answer), kept so they survive the
  // handoff into the wizard. Only trusted, allowlisted-origin messages reach it.
  let wizardOnlyPrefill: Record<string, string> = {};
  let disposeAutofill: () => void = () => {};
  const routeToWizard = (): void => {
    if (routed) return;
    routed = true;
    // Read the form before it is replaced: an accepted prefill from an exact
    // allowlisted origin has already been applied to these controls, so the
    // refinance answers carry into the wizard instead of being lost. Values
    // from a rejected origin were never applied and cannot be read here.
    const initialValues = readRefinancePrefillFromForm(form, wizardOnlyPrefill);
    disposeAutofill();
    renderRefinanceWizard(root, {
      initialValues,
      submission: refinanceSubmissionDeps(config, fetchFn, telemetrySink),
    });
  };

  disposeAutofill = installAutofillListener({
    allowedOrigins: config.allowedPrefillOrigins,
    root: form,
    onMapped: (mapped) => {
      wizardOnlyPrefill = translateLegacyMilitaryPrefill(mapped);
    },
    onBeforeApply: () => {
      applyingPrefill = true;
    },
    onApplied: (fields) => {
      applyingPrefill = false;
      if (fields.length > 0) showPrefillNotice(root, fields.length);
      // A prefill that sets loan purpose to refinance routes into the wizard,
      // matching the query-hint and in-form selection paths.
      const purpose = form.querySelector<HTMLSelectElement>("#loanPurpose")?.value;
      if (shouldRouteToWizard(purpose)) routeToWizard();
    },
    onAck: (outcome, event) => {
      // Acknowledge back to the exact sender origin only (never a wildcard).
      notifyEmbedder(window, buildPrefillAck(outcome), [event.origin]);
    },
  });

  // In-form selection: choosing "Refinance" in the live control enters the
  // wizard rather than staying in the legacy long form.
  const purposeSelect = form.querySelector<HTMLSelectElement>("#loanPurpose");
  purposeSelect?.addEventListener("change", () => {
    if (applyingPrefill) return;
    if (shouldRouteToWizard(purposeSelect.value)) routeToWizard();
  });

  // Announce readiness once the inbound listener is installed, fanning out to
  // every configured allowed origin with an exact targetOrigin. No-op when the
  // allowlist is empty.
  notifyEmbedder(
    window,
    buildReadyMessage({ environment: config.environment }),
    config.allowedPrefillOrigins,
  );
}

if (typeof document !== "undefined") {
  const appRoot = document.getElementById("app");
  if (appRoot) {
    void bootstrap(appRoot);
  }
}
